import React, { useState, useEffect } from 'react';
import { Form, Input, Button, notification } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { USERS_API, HITECH_CONFIG } from './api';

const MODERATOR_ROLES = ['moderator', 'admin', 'super'];

const AdminLogin = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (localStorage.getItem('hitech_admin_token')) {
      navigate('/admin/applications', { replace: true });
    }
  }, [navigate]);

  const onFinish = async ({ email, password }) => {
    setLoading(true);
    try {
      const res = await fetch(`${USERS_API}/signin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user: { email, password },
          portal: 'admin',
          supergroup: HITECH_CONFIG.supergroupId,
          omitCookies: true,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.token) {
        const errCode = data?.error?.code || '';
        const msg = errCode === 'auth/wrong-password'
          ? 'Incorrect password'
          : errCode === 'auth/user-not-found'
          ? 'No account found with this email'
          : data.message || 'Login failed';
        notification.error({ message: msg });
        return;
      }

      const userRole = data?.user?.role;
      if (!MODERATOR_ROLES.includes(userRole)) {
        notification.error({ message: 'Access denied. Your account does not have admin permissions.' });
        return;
      }

      localStorage.setItem('hitech_admin_token', data.token);
      localStorage.setItem('hitech_admin_user', JSON.stringify({ name: data?.user?.name, email: data?.user?.email, role: userRole }));
      navigate('/admin/applications', { replace: true });
    } catch {
      notification.error({ message: 'Unable to reach server. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logo}>
          <img
            src="/Sources/images/logo/hitech_logo.png"
            alt="Hitech"
            style={{ height: 52, objectFit: 'contain' }}
            onError={e => { e.target.style.display = 'none'; }}
          />
          <h2 style={styles.title}>Admin Portal</h2>
          <p style={styles.subtitle}>Hitech Projects Pvt. Ltd.</p>
        </div>
        <Form layout="vertical" onFinish={onFinish} autoComplete="off">
          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Enter your email' },
              { type: 'email', message: 'Enter a valid email' },
            ]}
          >
            <Input prefix={<UserOutlined />} placeholder="Email address" size="large" />
          </Form.Item>
          <Form.Item name="password" rules={[{ required: true, message: 'Enter your password' }]}>
            <Input.Password prefix={<LockOutlined />} placeholder="Password" size="large" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              size="large"
              block
              style={styles.btn}
            >
              Sign In
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #0d2e61 0%, #1a4a8a 100%)',
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    padding: '40px 36px',
    width: '100%',
    maxWidth: 400,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  logo: { textAlign: 'center', marginBottom: 32 },
  title: { color: '#0d2e61', fontWeight: 700, fontSize: 24, margin: '12px 0 4px' },
  subtitle: { color: '#888', fontSize: 13, margin: 0 },
  btn: { background: '#0d2e61', borderColor: '#0d2e61', fontWeight: 600, height: 44 },
};

export default AdminLogin;
